import * as React from "react"
import { cn } from "@/lib/utils"

interface ResponsiveGridProps {
  children: React.ReactNode
  cols?: {
    default?: number
    sm?: number
    md?: number
    lg?: number
    xl?: number
  }
  gap?: number
  className?: string
}

export const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
  children,
  cols = { default: 1, md: 2, lg: 3 },
  gap = 4,
  className,
}) => {
  const gridClasses = cn(
    "grid",
    `gap-${gap}`,
    cols.default && `grid-cols-${cols.default}`,
    cols.sm && `sm:grid-cols-${cols.sm}`,
    cols.md && `md:grid-cols-${cols.md}`,
    cols.lg && `lg:grid-cols-${cols.lg}`,
    cols.xl && `xl:grid-cols-${cols.xl}`,
    className
  )

  return <div className={gridClasses}>{children}</div>
}

interface ResponsiveContainerProps {
  children: React.ReactNode
  size?: "sm" | "md" | "lg" | "xl" | "full"
  className?: string
}

export const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  children,
  size = "lg",
  className,
}) => {
  const containerClasses = cn(
    "mx-auto px-3 sm:px-6 lg:px-8",
    {
      "max-w-3xl": size === "sm",
      "max-w-4xl": size === "md",
      "max-w-6xl": size === "lg",
      "max-w-7xl": size === "xl",
      "max-w-none": size === "full",
    },
    className
  )

  return <div className={containerClasses}>{children}</div>
}

interface ResponsiveStackProps {
  children: React.ReactNode
  direction?: "vertical" | "horizontal"
  spacing?: number
  align?: "start" | "center" | "end" | "stretch"
  justify?: "start" | "center" | "end" | "between" | "around" | "evenly"
  wrap?: boolean
  className?: string
}

export const ResponsiveStack: React.FC<ResponsiveStackProps> = ({
  children,
  direction = "vertical",
  spacing = 4,
  align = "stretch",
  justify = "start",
  wrap = false,
  className,
}) => {
  const stackClasses = cn(
    "flex",
    {
      "flex-col": direction === "vertical",
      "flex-row": direction === "horizontal",
      "flex-wrap": wrap,
      // Spacing
      [`space-y-${spacing}`]: direction === "vertical",
      [`space-x-${spacing}`]: direction === "horizontal",
      // Alignment
      "items-start": align === "start",
      "items-center": align === "center",
      "items-end": align === "end",
      "items-stretch": align === "stretch",
      // Justification
      "justify-start": justify === "start",
      "justify-center": justify === "center",
      "justify-end": justify === "end",
      "justify-between": justify === "between",
      "justify-around": justify === "around",
      "justify-evenly": justify === "evenly",
    },
    className
  )

  return <div className={stackClasses}>{children}</div>
}

interface ResponsiveColumnsProps {
  children: React.ReactNode
  leftColumn: React.ReactNode
  rightColumn?: React.ReactNode
  leftWidth?: "1/4" | "1/3" | "1/2" | "2/3" | "3/4"
  gap?: number
  stackOnMobile?: boolean
  className?: string
}

export const ResponsiveColumns: React.FC<ResponsiveColumnsProps> = ({
  children,
  leftColumn,
  rightColumn,
  leftWidth = "1/3",
  gap = 6,
  stackOnMobile = true,
  className,
}) => {
  const containerClasses = cn(
    "grid",
    `gap-${gap}`,
    {
      "grid-cols-1": stackOnMobile,
      "lg:grid-cols-4": leftWidth === "1/4",
      "lg:grid-cols-3": leftWidth === "1/3",
      "lg:grid-cols-2": leftWidth === "1/2",
      "md:grid-cols-3": leftWidth === "2/3",
      "md:grid-cols-4": leftWidth === "3/4",
    },
    !stackOnMobile && {
      "grid-cols-4": leftWidth === "1/4",
      "grid-cols-3": leftWidth === "1/3",
      "grid-cols-2": leftWidth === "1/2",
    },
    className
  )

  const leftClasses = cn({
    "lg:col-span-1": leftWidth === "1/4" || leftWidth === "1/3",
    "lg:col-span-2": leftWidth === "1/2" || leftWidth === "2/3",
    "lg:col-span-3": leftWidth === "3/4",
  })

  const rightClasses = cn({
    "lg:col-span-3": leftWidth === "1/4",
    "lg:col-span-2": leftWidth === "1/3" || leftWidth === "1/2",
    "lg:col-span-1": leftWidth === "2/3" || leftWidth === "3/4",
  })

  return (
    <div className={containerClasses}>
      <div className={leftClasses}>{leftColumn}</div>
      {rightColumn ? (
        <div className={rightClasses}>{rightColumn}</div>
      ) : (
        <div className={rightClasses}>{children}</div>
      )}
    </div>
  )
}